/**
 * Performance monitoring system for Chromis print simulation
 * Tracks web vitals and custom substrate engine metrics
 */

import { onCLS, onINP, onFCP, onLCP } from "web-vitals";
import { analyzeBrowserSupport } from "./browserSupport";

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

interface SubstrateMetric {
  operation: string;
  duration: number;
  substrateId?: string;
  success: boolean;
  memoryUsage?: number;
}

interface AccuracyMetric {
  operation: string;
  colorInput: string;
  substrateId?: string;
  deltaE: number;
  targetDeltaE: number;
  passed: boolean;
  timestamp: number;
}

interface UsageMetric {
  feature: string;
  action: string;
  timestamp: number;
  metadata?: Record<string, any>;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private substrateMetrics: SubstrateMetric[] = [];
  private accuracyMetrics: AccuracyMetric[] = [];
  private usageMetrics: UsageMetric[] = [];
  private observers: PerformanceObserver[] = [];

  constructor() {
    this.initWebVitals();
    this.initCustomMetrics();
  }

  // Initialize web vitals monitoring
  private initWebVitals() {
    const sendMetric = (metric: any) => {
      this.recordMetric({
        name: metric.name,
        value: metric.value,
        timestamp: Date.now(),
        metadata: {
          rating: metric.rating,
          delta: metric.delta,
          id: metric.id,
        },
      });
    };

    onCLS(sendMetric);
    onINP(sendMetric);
    onFCP(sendMetric);
    onLCP(sendMetric);

    // TTFB was removed in web-vitals v3+, implement manually
    this.measureTTFB(sendMetric);
  }

  // Measure TTFB manually (removed in web-vitals v3+)
  private measureTTFB(sendMetric: (metric: any) => void) {
    if (typeof window !== "undefined" && window.performance) {
      const navigation = performance.getEntriesByType(
        "navigation",
      )[0] as PerformanceNavigationTiming;
      if (navigation) {
        const ttfb = navigation.responseStart - navigation.requestStart;
        sendMetric({
          name: "TTFB",
          value: ttfb,
          rating:
            ttfb < 800 ? "good" : ttfb < 1800 ? "needs-improvement" : "poor",
          delta: ttfb,
          id: "v3-web-vitals",
        });
      }
    }
  }

  // Initialize custom performance observers
  private initCustomMetrics() {
    // Monitor long tasks
    if ("PerformanceObserver" in window) {
      const longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.recordMetric({
            name: "long-task",
            value: entry.duration,
            timestamp: entry.startTime,
            metadata: {
              name: (entry as any).name || "unknown",
            },
          });
        }
      });
      longTaskObserver.observe({ entryTypes: ["longtask"] });
      this.observers.push(longTaskObserver);
    }

    // Monitor resource timing
    if ("PerformanceObserver" in window) {
      const resourceObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.recordMetric({
            name: "resource-load",
            value: entry.duration,
            timestamp: entry.startTime,
            metadata: {
              name: (entry as any).name,
              size: (entry as any).transferSize,
              type: (entry as any).initiatorType,
            },
          });
        }
      });
      resourceObserver.observe({ entryTypes: ["resource"] });
      this.observers.push(resourceObserver);
    }
  }

  // Record a general performance metric
  recordMetric(metric: PerformanceMetric) {
    this.metrics.push(metric);

    // Keep only last 100 metrics to prevent memory leaks
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.log("Performance Metric:", metric);
    }
  }

  // Record substrate engine operation
  recordSubstrateOperation(metric: SubstrateMetric) {
    this.substrateMetrics.push(metric);

    // Keep only last 50 substrate metrics
    if (this.substrateMetrics.length > 50) {
      this.substrateMetrics = this.substrateMetrics.slice(-50);
    }

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.log("Substrate Operation:", metric);
    }
  }

  // Measure substrate engine performance
  measureSubstrateOperation<T>(
    operation: string,
    substrateId: string | undefined,
    fn: () => T,
  ): T {
    const startTime = performance.now();
    const startMemory = this.getMemoryUsage();

    try {
      const result = fn();
      const duration = performance.now() - startTime;
      const endMemory = this.getMemoryUsage();

      this.recordSubstrateOperation({
        operation,
        duration,
        substrateId,
        success: true,
        memoryUsage: endMemory ? endMemory - (startMemory || 0) : undefined,
      });

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;

      this.recordSubstrateOperation({
        operation,
        duration,
        substrateId,
        success: false,
      });

      throw error;
    }
  }

  // Async version of substrate operation measurement
  async measureSubstrateOperationAsync<T>(
    operation: string,
    substrateId: string | undefined,
    fn: () => Promise<T>,
  ): Promise<T> {
    const startTime = performance.now();
    const startMemory = this.getMemoryUsage();

    try {
      const result = await fn();
      const duration = performance.now() - startTime;
      const endMemory = this.getMemoryUsage();

      this.recordSubstrateOperation({
        operation,
        duration,
        substrateId,
        success: true,
        memoryUsage: endMemory ? endMemory - (startMemory || 0) : undefined,
      });

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;

      this.recordSubstrateOperation({
        operation,
        duration,
        substrateId,
        success: false,
      });

      throw error;
    }
  }

  // Get memory usage if available
  private getMemoryUsage(): number | undefined {
    if ("memory" in performance) {
      return (performance as any).memory.usedJSHeapSize;
    }
    return undefined;
  }

  // Get performance summary
  getPerformanceSummary() {
    const webVitals = this.metrics.filter((m) =>
      ["CLS", "INP", "FCP", "LCP", "TTFB"].includes(m.name),
    );

    const substrateOps = this.substrateMetrics;
    const avgSubstrateTime =
      substrateOps.length > 0
        ? substrateOps.reduce((sum, op) => sum + op.duration, 0) /
          substrateOps.length
        : 0;

    const successRate =
      substrateOps.length > 0
        ? substrateOps.filter((op) => op.success).length / substrateOps.length
        : 1;

    return {
      webVitals: webVitals.reduce(
        (acc, metric) => {
          acc[metric.name] = metric.value;
          return acc;
        },
        {} as Record<string, number>,
      ),
      substrateOperations: {
        total: substrateOps.length,
        averageDuration: avgSubstrateTime,
        successRate: successRate,
        averageMemoryUsage:
          substrateOps
            .filter((op) => op.memoryUsage !== undefined)
            .reduce((sum, op) => sum + (op.memoryUsage || 0), 0) /
            substrateOps.filter((op) => op.memoryUsage !== undefined).length ||
          0,
      },
      longTasks: this.metrics.filter((m) => m.name === "long-task").length,
    };
  }

  // Record accuracy metric (ΔE measurement)
  recordAccuracyMetric(metric: AccuracyMetric) {
    this.accuracyMetrics.push(metric);

    // Keep only last 50 accuracy metrics
    if (this.accuracyMetrics.length > 50) {
      this.accuracyMetrics = this.accuracyMetrics.slice(-50);
    }

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.log("Accuracy Metric:", metric);
    }
  }

  // Record usage metric
  recordUsageMetric(metric: UsageMetric) {
    this.usageMetrics.push(metric);

    // Keep only last 100 usage metrics
    if (this.usageMetrics.length > 100) {
      this.usageMetrics = this.usageMetrics.slice(-100);
    }

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.log("Usage Metric:", metric);
    }
  }

  // Measure color accuracy with ΔE calculation
  measureColorAccuracy(
    operation: string,
    originalColor: string,
    simulatedColor: string,
    substrateId?: string,
    targetDeltaE: number = 2.0,
  ): number {
    // Simple ΔE calculation (CIE76 formula)
    const deltaE = this.calculateDeltaE(originalColor, simulatedColor);
    const passed = deltaE <= targetDeltaE;

    this.recordAccuracyMetric({
      operation,
      colorInput: originalColor,
      substrateId,
      deltaE,
      targetDeltaE,
      passed,
      timestamp: Date.now(),
    });

    return deltaE;
  }

  // Simple ΔE calculation using CIE76 formula
  private calculateDeltaE(color1: string, color2: string): number {
    // Convert hex to RGB
    const rgb1 = this.hexToRgb(color1);
    const rgb2 = this.hexToRgb(color2);

    if (!rgb1 || !rgb2) return Infinity;

    // Convert RGB to XYZ (simplified)
    const xyz1 = this.rgbToXyz(rgb1.r, rgb1.g, rgb1.b);
    const xyz2 = this.rgbToXyz(rgb2.r, rgb2.g, rgb2.b);

    // Convert XYZ to Lab
    const lab1 = this.xyzToLab(xyz1.x, xyz1.y, xyz1.z);
    const lab2 = this.xyzToLab(xyz2.x, xyz2.y, xyz2.z);

    // Calculate ΔE (CIE76)
    const deltaL = lab1.l - lab2.l;
    const deltaA = lab1.a - lab2.a;
    const deltaB = lab1.b - lab2.b;

    return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
  }

  // Helper: Convert hex to RGB
  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  // Helper: Convert RGB to XYZ (simplified)
  private rgbToXyz(r: number, g: number, b: number) {
    // Normalize RGB values
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    // Apply gamma correction
    const rLinear =
      rNorm > 0.04045 ? Math.pow((rNorm + 0.055) / 1.055, 2.4) : rNorm / 12.92;
    const gLinear =
      gNorm > 0.04045 ? Math.pow((gNorm + 0.055) / 1.055, 2.4) : gNorm / 12.92;
    const bLinear =
      bNorm > 0.04045 ? Math.pow((bNorm + 0.055) / 1.055, 2.4) : bNorm / 12.92;

    // Convert to XYZ (sRGB D65)
    return {
      x: rLinear * 0.4124564 + gLinear * 0.3575761 + bLinear * 0.1804375,
      y: rLinear * 0.2126729 + gLinear * 0.7151522 + bLinear * 0.072175,
      z: rLinear * 0.0193339 + gLinear * 0.119192 + bLinear * 0.9503041,
    };
  }

  // Helper: Convert XYZ to Lab
  private xyzToLab(x: number, y: number, z: number) {
    // D65 white point
    const xn = x / 0.95047;
    const yn = y / 1.0;
    const zn = z / 1.08883;

    const fx = xn > 0.008856 ? Math.pow(xn, 1 / 3) : 7.787 * xn + 16 / 116;
    const fy = yn > 0.008856 ? Math.pow(yn, 1 / 3) : 7.787 * yn + 16 / 116;
    const fz = zn > 0.008856 ? Math.pow(zn, 1 / 3) : 7.787 * zn + 16 / 116;

    return {
      l: 116 * fy - 16,
      a: 500 * (fx - fy),
      b: 200 * (fy - fz),
    };
  }

  // Get accuracy summary
  getAccuracySummary() {
    if (this.accuracyMetrics.length === 0) {
      return {
        totalMeasurements: 0,
        averageDeltaE: 0,
        passRate: 1.0,
        belowTarget2: 0,
        belowTarget5: 0,
      };
    }

    const total = this.accuracyMetrics.length;
    const averageDeltaE =
      this.accuracyMetrics.reduce((sum, m) => sum + m.deltaE, 0) / total;
    const passRate =
      this.accuracyMetrics.filter((m) => m.passed).length / total;
    const belowTarget2 = this.accuracyMetrics.filter(
      (m) => m.deltaE <= 2.0,
    ).length;
    const belowTarget5 = this.accuracyMetrics.filter(
      (m) => m.deltaE <= 5.0,
    ).length;

    return {
      totalMeasurements: total,
      averageDeltaE: Math.round(averageDeltaE * 100) / 100,
      passRate: Math.round(passRate * 100) / 100,
      belowTarget2,
      belowTarget5,
    };
  }

  // Get usage analytics
  getUsageAnalytics() {
    const featureCounts: Record<string, number> = {};
    const actionCounts: Record<string, number> = {};

    this.usageMetrics.forEach((metric) => {
      featureCounts[metric.feature] = (featureCounts[metric.feature] || 0) + 1;
      actionCounts[metric.action] = (actionCounts[metric.action] || 0) + 1;
    });

    return {
      totalActions: this.usageMetrics.length,
      featureCounts,
      actionCounts,
      mostUsedFeature:
        Object.entries(featureCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ||
        "none",
    };
  }

  // Get browser support analysis
  async getBrowserSupportAnalysis() {
    const currentMetrics = {
      fcp: this.metrics.find((m) => m.name === "FCP")?.value || 0,
      lcp: this.metrics.find((m) => m.name === "LCP")?.value || 0,
      inp: this.metrics.find((m) => m.name === "INP")?.value || 0,
      cls: this.metrics.find((m) => m.name === "CLS")?.value || 0,
      ttfb: this.metrics.find((m) => m.name === "TTFB")?.value || 0,
      simulationTime:
        this.substrateMetrics.length > 0
          ? this.substrateMetrics.reduce((sum, m) => sum + m.duration, 0) /
            this.substrateMetrics.length
          : 0,
      memoryUsage:
        this.substrateMetrics.length > 0
          ? this.substrateMetrics
              .filter((m) => m.memoryUsage !== undefined)
              .reduce((sum, m) => sum + (m.memoryUsage || 0), 0) /
            (this.substrateMetrics.filter((m) => m.memoryUsage !== undefined)
              .length || 0)
          : 0,
    };

    return await analyzeBrowserSupport(currentMetrics);
  }

  // Export metrics for analysis
  async exportMetrics() {
    const browserSupport = await this.getBrowserSupportAnalysis();

    return {
      timestamp: Date.now(),
      metrics: this.metrics,
      substrateMetrics: this.substrateMetrics,
      accuracyMetrics: this.accuracyMetrics,
      usageMetrics: this.usageMetrics,
      summary: this.getPerformanceSummary(),
      accuracySummary: this.getAccuracySummary(),
      usageAnalytics: this.getUsageAnalytics(),
      browserSupport,
    };
  }

  // Cleanup observers
  cleanup() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Export convenience functions
export const measureSubstrateOperation = <T>(
  operation: string,
  substrateId: string | undefined,
  fn: () => T,
) => performanceMonitor.measureSubstrateOperation(operation, substrateId, fn);

export const measureSubstrateOperationAsync = <T>(
  operation: string,
  substrateId: string | undefined,
  fn: () => Promise<T>,
) =>
  performanceMonitor.measureSubstrateOperationAsync(operation, substrateId, fn);

export const getPerformanceSummary = () =>
  performanceMonitor.getPerformanceSummary();
export const exportMetrics = () => performanceMonitor.exportMetrics();
